// src/pages/PublicChatPage.js - Public guest-facing chat interface
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { publicChatService } from '../services/api';

const PublicChatPage = () => {
  const { chatToken } = useParams();
  const [chat, setChat] = useState(null);
  const [event, setEvent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Initial load
  useEffect(() => {
    const loadChat = async () => {
      try {
        setLoading(true);
        const response = await publicChatService.getChat(chatToken);
        setChat(response.chat);
        setEvent(response.event);
        setMessages(response.chat.messages || []);
      } catch (err) {
        console.error('Error loading chat:', err);
        if (err.response?.status === 404) {
          setError('This chat link is invalid or has expired.');
        } else {
          setError('Failed to load chat. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (chatToken) {
      loadChat();
    }
  }, [chatToken]);

  // Poll for new messages
  useEffect(() => {
    if (!chatToken || error) return;

    const pollInterval = setInterval(async () => {
      try {
        const lastMessage = messages[messages.length - 1];
        const since = lastMessage?.timestamp;
        const response = await publicChatService.pollMessages(chatToken, since);

        if (response.hasNew && response.messages.length > 0) {
          // Only add truly new messages
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m._id));
            const newMsgs = response.messages.filter(m => !existingIds.has(m._id));
            return [...prev, ...newMsgs];
          });
        }
      } catch (err) {
        console.error('Error polling messages:', err);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [chatToken, messages, error]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim() || sending) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Optimistically add message
    const optimisticMsg = {
      _id: `temp-${Date.now()}`,
      sender: 'guest',
      content: messageContent,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, optimisticMsg]);

    try {
      const response = await publicChatService.sendMessage(chatToken, messageContent);

      // Replace optimistic message with real one
      setMessages(prev =>
        prev.map(m => m._id === optimisticMsg._id ? response.message : m)
      );
    } catch (err) {
      console.error('Error sending message:', err);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m._id !== optimisticMsg._id));

      if (err.response?.status === 429) {
        alert('Please wait a moment before sending more messages.');
      } else {
        alert('Failed to send message. Please try again.');
      }
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [chatToken, newMessage, sending]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  // Group messages by date
  const groupMessagesByDate = (msgs) => {
    const groups = [];
    let currentDate = null;

    msgs.forEach(msg => {
      const msgDate = new Date(msg.timestamp).toDateString();
      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ type: 'date', date: msg.timestamp });
      }
      groups.push({ type: 'message', ...msg });
    });

    return groups;
  };

  if (loading) {
    return (
      <div className="public-chat-loading">
        <div className="public-chat-spinner"></div>
        <p>Loading chat...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-chat-error">
        <div className="public-chat-error-icon">!</div>
        <h2>Chat Not Available</h2>
        <p>{error}</p>
      </div>
    );
  }

  const groupedMessages = groupMessagesByDate(messages);

  return (
    <div className="public-chat-container">
      {/* Header */}
      <div className="public-chat-header">
        <div className="public-chat-header-content">
          <div className="public-chat-avatar">
            <PersonIcon />
          </div>
          <div className="public-chat-header-info">
            <h2>{event?.name || 'Event Chat'}</h2>
            {event?.date && (
              <p>
                <EventIcon className="public-chat-event-icon" />
                {new Date(event.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
                {event.venue && ` • ${event.venue}`}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="public-chat-messages">
        {messages.length === 0 ? (
          <div className="public-chat-empty">
            <p>No messages yet. Send a message to get started!</p>
          </div>
        ) : (
          groupedMessages.map((item, index) => {
            if (item.type === 'date') {
              return (
                <div key={`date-${index}`} className="public-chat-date-divider">
                  <span>{formatDate(item.date)}</span>
                </div>
              );
            }

            return (
              <div
                key={item._id || index}
                className={`public-chat-message ${item.sender === 'guest' ? 'sent' : 'received'}`}
              >
                <div className="public-chat-bubble">
                  <p>{item.content}</p>
                  <div className="public-chat-bubble-footer">
                    <span className="public-chat-time">{formatTime(item.timestamp)}</span>
                    {item.sender === 'guest' && (
                      item.readAt ? (
                        <DoneAllIcon className="public-chat-read-icon read" />
                      ) : (
                        <CheckIcon className="public-chat-read-icon" />
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="public-chat-input-container">
        <div className="public-chat-input-wrapper">
          <textarea
            ref={inputRef}
            className="public-chat-input"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            rows={1}
            disabled={sending}
          />
          <button
            className={`public-chat-send-btn ${newMessage.trim() ? 'active' : ''}`}
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || sending}
          >
            <SendIcon />
          </button>
        </div>
        <p className="public-chat-powered-by">
          Powered by <strong>Mandap.AI</strong>
        </p>
      </div>
    </div>
  );
};

export default PublicChatPage;
